import { Injectable } from '@angular/core';
import { McpContextSnapshot } from './mcp-context.service';

export interface McpResolvedAction {
    originalAction: string;
    action: string;
    params: Record<string, any>;
    corrected: boolean;
    confidence: number;
    unresolved: boolean;
    message?: string;
}

@Injectable({ providedIn: 'root' })
export class McpIntentRouterService {
    resolvePageAction(
        snapshot: McpContextSnapshot,
        requestedAction: string,
        params?: Record<string, any>
    ): McpResolvedAction {
        const originalAction = String(requestedAction ?? '').trim();
        const normalized = this.normalizeAction(originalAction);
        const nextParams = { ...(params ?? {}) };
        const availableActions = Array.isArray(snapshot?.actions)
            ? snapshot.actions.map((a) => String(a?.action ?? '').trim()).filter(Boolean)
            : [];
        const availableByNormalized = new Map<string, string>(
            availableActions.map((a) => [this.normalizeAction(a), a])
        );

        const exact = availableByNormalized.get(normalized);
        if (exact) {
            return {
                originalAction,
                action: exact,
                params: nextParams,
                corrected: false,
                confidence: 1,
                unresolved: false,
            };
        }

        const pageKey = this.getPageKey(snapshot, availableActions);
        const candidates = this.getCandidates(pageKey, normalized, nextParams);
        for (const candidate of candidates) {
            const matched = availableByNormalized.get(this.normalizeAction(candidate));
            if (matched) {
                return {
                    originalAction,
                    action: matched,
                    params: nextParams,
                    corrected: true,
                    confidence: 0.95,
                    unresolved: false,
                };
            }
        }

        return {
            originalAction,
            action: originalAction,
            params: nextParams,
            corrected: false,
            confidence: 0,
            unresolved: true,
            message: this.buildUnavailableMessage(originalAction, availableActions, pageKey),
        };
    }

    private getPageKey(
        snapshot: McpContextSnapshot,
        availableActions: string[]
    ): 'packages' | 'shipping' | 'receiving' | 'dashboard' | 'printer' | 'resources' | 'mailbox' | 'generic' {
        const normalizedActions = new Set(
            (availableActions ?? []).map((a) => this.normalizeAction(a))
        );

        // Signature-first detection is more reliable than route/page labels when
        // views are embedded, reused, or routed through shared shells.
        // Packages kiosk has BOTH receive + ship actions — detect it before those.
        if (
            normalizedActions.has('find_orders') ||
            (normalizedActions.has('open_order') &&
                normalizedActions.has('receive_with_labels') &&
                normalizedActions.has('create_label'))
        ) {
            return 'packages';
        }
        if (
            normalizedActions.has('toggle_unassigned_drawer') ||
            normalizedActions.has('open_unassigned_drawer') ||
            normalizedActions.has('close_unassigned_drawer') ||
            normalizedActions.has('assign_order') ||
            normalizedActions.has('unassign_order')
        ) {
            return 'resources';
        }
        if (
            normalizedActions.has('create_label') ||
            normalizedActions.has('mark_shipping_shipped') ||
            normalizedActions.has('print_shipping_label')
        ) {
            return 'shipping';
        }
        if (
            normalizedActions.has('open_receiving_order') ||
            normalizedActions.has('receive_with_labels') ||
            normalizedActions.has('filter_not_received')
        ) {
            return 'receiving';
        }
        if (
            normalizedActions.has('open_section') ||
            normalizedActions.has('show_today') ||
            normalizedActions.has('show_tomorrow')
        ) {
            return 'dashboard';
        }
        if (
            normalizedActions.has('print_label') ||
            normalizedActions.has('open_print_dialog')
        ) {
            return 'printer';
        }
        if (
            normalizedActions.has('open_related_orders_drawer') ||
            normalizedActions.has('open_new_order_drawer') ||
            normalizedActions.has('open_add_customer_drawer') ||
            normalizedActions.has('set_order_form_field')
        ) {
            return 'mailbox';
        }

        const route = String(snapshot?.route ?? '').toLowerCase();
        const page = String(snapshot?.pageLabel ?? '').toLowerCase();
        if (route.includes('/kiosk/packages') || page.includes('packages kiosk')) return 'packages';
        if (route.includes('/shipping') || page.includes('shipping')) return 'shipping';
        if (route.includes('/receiving') || page.includes('receiving')) return 'receiving';
        if (route.includes('/dashboard') || page.includes('operations')) return 'dashboard';
        if (route.includes('/printer-view') || page.includes('printer')) return 'printer';
        if (route.includes('/resources') || page.includes('resource')) return 'resources';
        if (route.includes('/mailbox') || route.includes('/mail/') || page.includes('mailbox') || page.includes('email')) return 'mailbox';
        return 'generic';
    }

    private normalizeAction(value: string): string {
        return String(value ?? '')
            .toLowerCase()
            .trim()
            .replace(/[()-]/g, '_')
            .replace(/[\s/]+/g, '_')
            .replace(/[^a-z0-9_]/g, '')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
    }

    private getCandidates(
        pageKey: 'packages' | 'shipping' | 'receiving' | 'dashboard' | 'printer' | 'resources' | 'mailbox' | 'generic',
        action: string,
        params: Record<string, any>
    ): string[] {
        const commonAliasMap: Record<string, string[]> = {
            close_order_drawer: ['close_drawer', 'close_panel'],
            close_receiving_order: ['close_drawer', 'close_panel'],
            close_shipping_order: ['close_drawer', 'close_panel'],
            close_panel: ['close_drawer', 'close_panel'],
        };
        const packagesAliasMap: Record<string, string[]> = {
            open_receiving_order: ['open_order', 'open_incoming_order'],
            open_shipping_order: ['open_order', 'open_outgoing_order'],
            open_drawer: ['open_order'],
            receive_order: ['open_order', 'open_incoming_order'],
            close_drawer: ['close_panel'],
            close_order_drawer: ['close_panel'],
        };
        const shippingAliasMap: Record<string, string[]> = {
            set_and_save_box: ['create_label'],
            set_box_and_save: ['create_label'],
            save_box: ['create_label'],
            set_box_dimension: ['set_box_dimensions'],
            set_box_dimensions: ['set_box_dimensions'],
            set_dimensions: ['set_box_dimensions'],
            set_box_size: ['set_box_dimensions'],
            set_next_box_dimension: ['set_next_box_dimensions'],
            set_next_box_dimensions: ['set_next_box_dimensions'],
            continue_box_dimensions: ['set_next_box_dimensions'],
            fill_next_box: ['set_next_box_dimensions'],
            set_multiple_boxes: ['set_multiple_box_dimensions'],
            set_multiple_box_dimensions: ['set_multiple_box_dimensions'],
            ship_it: ['mark_shipping_shipped'],
            mark_shipped: ['mark_shipping_shipped'],
            mark_as_shipped: ['mark_shipping_shipped'],
            print_label: ['print_first_generated_label'],
            print_shipping: ['print_first_generated_label'],
            print_box_label: ['print_shipping_label'],
            print_label_for_box: ['print_shipping_label'],
        };
        const receivingAliasMap: Record<string, string[]> = {
            open_order: ['open_receiving_order'],
            receive_order: ['open_receiving_order'],
            close_order_drawer: ['close_drawer'],
            clear_filters_and_search: ['clear_filters'],
        };
        const dashboardAliasMap: Record<string, string[]> = {
            set_tomorrow: ['show_tomorrow'],
            tomorrow: ['show_tomorrow'],
            set_today: ['show_today'],
            today: ['show_today'],
            to_due_today: ['show_today'],
            open_drawer: ['open_order'],
        };
        const printerAliasMap: Record<string, string[]> = {
            close_printer_drawer: ['close_drawer'],
            print_label: ['print_shipping_label'],
            completion: ['open_completion_dialog'],
            open_completion: ['open_completion_dialog'],
            open_completion_drawer: ['open_completion_dialog'],
            open_check_dialog: ['open_completion_dialog'],
            close_completion: ['close_completion_dialog'],
            close_completion_drawer: ['close_completion_dialog'],
        };
        const resourcesAliasMap: Record<string, string[]> = {
            toggle_unassigned: ['toggle_unassigned_drawer'],
            open_unassigned: ['open_unassigned_drawer'],
            close_unassigned: ['close_unassigned_drawer'],
            unassign: ['unassign_order'],
            assign: ['assign_order'],
            move: ['move_order'],
        };
        const mailboxAliasMap: Record<string, string[]> = {
            related_orders: ['open_related_orders_drawer'],
            open_related_orders: ['open_related_orders_drawer'],
            view_related_orders: ['open_related_orders_drawer'],
            close_related_orders: ['close_related_orders_drawer'],
            close_related_orders_drawer: ['close_related_orders_drawer'],
            next_related_orders_page: ['next_page'],
            previous_related_orders_page: ['previous_page'],
            edit_order: ['open_edit_order_drawer'],
            open_edit_order: ['open_edit_order_drawer'],
            edit_order_drawer: ['open_edit_order_drawer'],
            new_order: ['open_new_order_drawer'],
            start_new_order: ['open_new_order_drawer'],
            new_order_for_customer: ['open_new_order_for_customer'],
            create_customer: ['open_add_customer_drawer'],
            add_customer: ['open_add_customer_drawer'],
            open_add_customer: ['open_add_customer_drawer'],
            set_order_field: ['set_order_form_field'],
            set_order_fields: ['set_order_form_fields'],
            set_form_field: ['set_order_form_field'],
            set_order_basics: ['set_order_basics'],
            order_tab: ['open_order_tab'],
            open_order_tab: ['open_order_tab'],
            go_to_tab: ['open_order_tab'],
            next_order_tab: ['next_order_tab'],
            previous_order_tab: ['previous_order_tab'],
            set_order_name: ['set_order_form_field'],
            set_order_date: ['set_order_form_field'],
            set_order_type: ['set_order_form_field'],
            save_order: ['save_order_form'],
            save_order_form: ['save_order_form'],
            close_order_drawer: ['close_order_drawer'],
        };

        const candidates: string[] = [];
        const pageMap =
            pageKey === 'packages'
                ? packagesAliasMap
                : pageKey === 'shipping'
                ? shippingAliasMap
                : pageKey === 'receiving'
                ? receivingAliasMap
                : pageKey === 'dashboard'
                ? dashboardAliasMap
                : pageKey === 'printer'
                ? printerAliasMap
                : pageKey === 'resources'
                ? resourcesAliasMap
                : pageKey === 'mailbox'
                ? mailboxAliasMap
                : {};
        if (commonAliasMap[action]?.length) {
            candidates.push(...commonAliasMap[action]);
        }
        if (pageMap[action]?.length) {
            candidates.push(...pageMap[action]);
        }

        if (action.startsWith('close_') || action.endsWith('_drawer')) {
            candidates.push('close_drawer');
        }

        if (pageKey === 'shipping' && action.includes('print')) {
            if (params?.['box'] != null) {
                candidates.push('print_shipping_label');
            } else {
                candidates.push('print_first_generated_label');
            }
        }

        if (pageKey === 'shipping' && action.includes('label') && (action.includes('create') || action.includes('generate'))) {
            candidates.push('create_label');
        }
        if (pageKey === 'receiving' && action.includes('receive')) {
            candidates.push('receive_with_labels');
        }
        if (pageKey === 'dashboard' && action.includes('tomorrow')) {
            candidates.push('show_tomorrow');
        }
        if (pageKey === 'dashboard' && action.includes('today')) {
            candidates.push('show_today');
        }
        if (pageKey === 'mailbox') {
            if (action === 'set_order_name') {
                params['name'] = params['name'] ?? params['value'] ?? params['order_name'] ?? '';
                candidates.push('set_order_form_field');
            }
            if (action === 'set_order_date') {
                params['date_order'] = params['date_order'] ?? params['value'] ?? params['date'] ?? '';
                candidates.push('set_order_form_field');
            }
            if (action === 'set_order_type') {
                params['order_type'] = params['order_type'] ?? params['value'] ?? params['type'] ?? '';
                candidates.push('set_order_form_field');
            }
        }

        return Array.from(new Set(candidates));
    }

    private buildUnavailableMessage(
        requestedAction: string,
        availableActions: string[],
        pageKey: 'packages' | 'shipping' | 'receiving' | 'dashboard' | 'printer' | 'resources' | 'mailbox' | 'generic'
    ): string {
        const preview = availableActions.slice(0, 8).join(', ');
        const pageLabel =
            pageKey === 'packages'
                ? 'packages kiosk'
                : pageKey === 'shipping'
                ? 'shipping'
                : pageKey === 'receiving'
                ? 'receiving'
                : pageKey === 'dashboard'
                ? 'operations dashboard'
                : pageKey === 'printer'
                ? 'printer'
                : pageKey === 'resources'
                ? 'resources'
                : pageKey === 'mailbox'
                ? 'mailbox'
                : 'current';
        return `I could not run "${requestedAction}" on this ${pageLabel} screen. Available actions include: ${preview}.`;
    }
}

